import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-forgiveness",
  title: "10 Bible verses about forgiveness, and what each asks",
  h1: "Bible verses about forgiveness",
  description:
    "Ten passages from the World English Bible with a note on each — including what forgiveness does not require, which is the part most of these pages leave out.",
  targets: [
    "bible verses about forgiveness",
    "how many times should i forgive bible",
    "scripture on forgiving someone who hurt you",
  ],
  published: "2026-08-27",
  standfirst:
    "Half of these are about being forgiven and half are about forgiving, and confusing the two is why the second half feels impossible.",
  takeaways: [
    "Seventy times seven in Matthew 18:22 is a refusal to count rather than a quota — Peter offered seven, which was already generous by the standards of the day.",
    "Forgiveness in these passages is the release of a debt, which is not the same as trust restored, reconciliation achieved, or a return to the situation that caused the harm.",
    "Matthew 6:14–15 ties the two directions together, and it is the most uncomfortable sentence in the collection; Mark 11:25 puts it in the middle of praying.",
    "Psalm 103:12 and Isaiah 43:25 describe God's forgiveness as distance and deliberate forgetting — pictures of removal rather than of overlooking.",
  ],
  intro: [
    {
      type: "p",
      text: "Two different things are being described in this category and they are usually printed together without a line between them. One is being forgiven — a debt released, sins removed as far as the east is from the west. The other is forgiving somebody who has actually harmed you, which is a considerably harder thing to be told to do, especially by a page.",
    },
    {
      type: "p",
      text: "It is also worth stating at the top what these passages do not say, because it is the omission that does the most damage. Forgiveness in the New Testament is the release of a debt. It is not the same as trusting the person again, not the same as reconciliation, which takes two, and not an instruction to return to a situation that is unsafe. Those distinctions are not softenings — they are visible in the texts themselves, and a page that blurs them ends up asking people to do something scripture never asked.",
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: "If we confess our sins, he is faithful and righteous to forgive us the sins, and to cleanse us from all unrighteousness.",
        ref: "1 John 1:9",
      },
      note: "The adjectives are the point: faithful and righteous, not merciful and reluctant. The forgiveness is described as consistent with the character of the one giving it rather than as an exception being made — which means the anxious question of whether it will be extended this time has already been answered in the wrong tense.",
    },
    {
      verse: {
        text: "As far as the east is from the west, so far has he removed our transgressions from us.",
        ref: "Psalm 103:12",
      },
      note: "A deliberately unmeasurable distance. North and south have poles and therefore a maximum; east and west do not, which is very unlikely to be an accident in a text this carefully made. The verb is removed — relocation rather than concealment.",
    },
    {
      verse: {
        text: "I, even I, am he who blots out your transgressions for my own sake; and I will not remember your sins.",
        ref: "Isaiah 43:25",
      },
      note: "Two things worth noticing. For my own sake — the motive is stated as being about the forgiver, which removes the question of whether you have earned it. And will not remember is a decision rather than an incapacity; it is a promise not to bring something up again, which is exactly what people mean when they say somebody has genuinely forgiven them.",
    },
    {
      verse: {
        text: "Then Peter came and said to him, “Lord, how often shall my brother sin against me, and I forgive him? Until seven times?” Jesus said to him, “I don't tell you until seven times, but, until seventy times seven.”",
        ref: "Matthew 18:21–22",
      },
      note: "Peter's seven was already generous — he is not being stingy, he is trying to be impressive. The answer is not a higher number but the collapse of counting, and anyone who has kept a mental tally of somebody's offences knows exactly what is being taken away. The parable that follows is about a servant forgiven an unpayable debt who then throttles a colleague over a small one.",
    },
    {
      verse: {
        text: "And be kind to one another, tender hearted, forgiving each other, just as God also in Christ forgave you.",
        ref: "Ephesians 4:32",
      },
      note: "The order in the sentence is the argument: the forgiving is modelled on something already received, not generated from your own reserves. Read the other way round — forgive in order to be forgiven — it becomes a transaction nobody can afford, and the verse is careful to put the receiving first.",
    },
    {
      verse: {
        text: "Bearing with one another, and forgiving each other, if any man has a complaint against any; even as Christ forgave you, so you also do.",
        ref: "Colossians 3:13",
      },
      note: "Bearing with is the neglected half. It names the ordinary, unglamorous work of tolerating people who are irritating rather than guilty, which is most of what living alongside anybody consists of. The complaint clause concedes that the complaint may be entirely legitimate; forgiving is not a verdict that nothing happened.",
    },
    {
      verse: {
        text: "For if you forgive men their trespasses, your heavenly Father will also forgive you. But if you don't forgive men their trespasses, neither will your Father forgive your trespasses.",
        ref: "Matthew 6:14–15",
      },
      note: "The hardest sentence here and it should not be softened. It follows immediately after the Lord's Prayer, which contains the same link in its own words. What it is not is a mechanism for earning: the whole surrounding teaching assumes a forgiveness already given, and this describes what happens to a person who receives it and refuses to pass any of it on. Held in a hard case, it is worth saying plainly that an unwillingness that has not yet become a refusal is exactly what the next verse is for.",
    },
    {
      verse: {
        text: "Whenever you stand praying, forgive, if you have anything against anyone; so that your Father, who is in heaven, may also forgive you your transgressions.",
        ref: "Mark 11:25",
      },
      note: "The practical companion to the verse above, and it puts forgiveness in a specific place: in the middle of praying, whenever you stand there, as something done then rather than resolved in the abstract. That makes it repeatable, which matters — most people do not forgive a serious injury once; they forgive it many times, in prayer, as it keeps coming back.",
    },
    {
      verse: {
        text: "Jesus said, “Father, forgive them, for they don't know what they are doing.”",
        ref: "Luke 23:34",
      },
      note: "Said during the injury rather than afterwards, and addressed to God rather than to the people responsible. That second detail is worth carrying: the prayer does not require a conversation with the person, their agreement, or their presence. It is the model for every situation in which the other party is unavailable, unrepentant, or dead.",
    },
    {
      verse: {
        text: "Don't seek revenge yourselves, beloved, but give place to God's wrath.",
        ref: "Romans 12:19",
      },
      note: "Often read as a demand to stop caring about justice, and it says the opposite: it assumes the wrong is real and that a reckoning belongs somewhere. What is being asked is a transfer of the case, not a dismissal of it. For anyone who has been told that forgiving means deciding it did not matter, this verse is the correction.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "what-it-does-not-require",
      text: "What forgiveness does not require",
    },
    {
      type: "ul",
      items: [
        "It does not require trust. Trust is rebuilt by evidence over time, and nothing here instructs you to hand it over on request.",
        "It does not require reconciliation, which takes two people and is not always available — Romans 12:18 says so explicitly, twice, in eleven words.",
        "It does not require returning to a situation that harmed you. Nothing in these passages asks that, and reading them as though they do has done real damage.",
        "It does not require deciding the wrong was small. Romans 12:19 assumes the opposite and transfers the case rather than closing it.",
        "It does not require feeling differently first. Mark 11:25 puts it in the middle of praying, which is a thing done rather than a thing felt.",
      ],
    },
    {
      type: "p",
      text: "What it does require is the release of the debt — giving up the right to collect. That is quite enough, it usually has to be done more than once, and it is closer to a repeated practice than to a single decision. If it is one of the things you find yourself circling at night, {the-collection-for-those-hours} is the neighbour of this page.",
      links: {
        "the-collection-for-those-hours":
          "/verses/bible-verses-for-anxiety-at-night",
      },
    },
    {
      type: "h2",
      id: "a-prayer-about-forgiveness",
      text: "A prayer from these passages",
    },
    {
      type: "p",
      text: "Father, forgive me as far as the east is from the west, and do not remember it. And where I am holding somebody's debt, help me put it down today — not because it was small, but because I am giving you the case. I will probably have to do this again tomorrow. Amen.",
    },
  ],
  faq: [
    {
      q: "How many times does the Bible say to forgive?",
      a: "Matthew 18:22 answers seventy times seven, in response to Peter's suggestion of seven. It is not a quota of 490 — it is the removal of counting altogether, which is why the parable that follows is about a servant who had been forgiven an unpayable debt and then went after a small one.",
    },
    {
      q: "Does forgiving someone mean I have to trust them again?",
      a: "No, and none of these passages says so. Forgiveness is the release of a debt; trust is rebuilt by evidence over time. Reconciliation requires two people, which Romans 12:18 concedes directly with its two qualifiers — if it is possible, and as much as it depends on you.",
    },
    {
      q: "What if I cannot forgive?",
      a: "Mark 11:25 locates it in prayer, whenever you stand there, which makes it repeatable rather than a single act you either manage or fail. For a serious injury most people find they forgive it many times over years. Being unable today is a different thing from refusing, and the passages are addressed to the second.",
    },
  ],
  related: [
    "morning-quiet-time-routine",
    "how-to-memorize-bible-verses",
    "digital-sabbath",
  ],
  relatedTopics: ["bible-verses-about-peace", "bible-verses-about-grief"],
};
